import { QueryClient } from "@tanstack/react-query";
import QueryKeys from "../api/queryKeys";
import {
  hgdbSMPWUserPermission,
  SMPWInvite,
  SMPWLocation,
  SMPWSchedule,
  SMPWUser,
  SMPWUserAvailability,
  SMPWZoneDefault,
} from "../types/smpw";
import { SMPWAPI } from "../api/smpw";
import { Congregation } from "../types/scheduling/weekend";

export const updateSMPWUserCache = (queryClient: QueryClient, newUser: SMPWUser, isNew = false) => {
  const newU = SMPWAPI.userFromResponse(newUser);
  if (isNew) {
    queryClient.setQueryData<SMPWUser[]>([QueryKeys.SMPWUsers], (old) => (old ? [...old, newU] : [newU]));
    return;
  }
  queryClient.setQueryData<SMPWUser[]>([QueryKeys.SMPWUsers], (old) =>
    old ? old.map((u) => (u.id === newU.id ? newU : u)) : [newU],
  );
};

export const updateSMPWUserPermissionsCache = (queryClient: QueryClient, newPerms: hgdbSMPWUserPermission[]) => {
  newPerms.forEach((newPerm) => {
    queryClient.setQueryData<hgdbSMPWUserPermission[]>([QueryKeys.SMPWUserPermissions], (old) => {
      if (!old) return [newPerm];
      if (!old.some((p) => p.user_id === newPerm.user_id)) return [...old, newPerm];
      return old.map((p) => (p.user_id === newPerm.user_id ? newPerm : p));
    });
  });
};

export function deleteFromSMPWUserCache(queryClient: QueryClient, user: SMPWUser) {
  queryClient.setQueriesData<SMPWUser[]>({ queryKey: [QueryKeys.SMPWUsers] }, (old) =>
    old ? old.filter((u) => u.id !== user.id) : [],
  );
}

export function deleteFromSMPWInviteCache(queryClient: QueryClient, invite: SMPWInvite) {
  queryClient.setQueriesData<SMPWInvite[]>({ queryKey: [QueryKeys.MySMPWInvites] }, (old) =>
    old ? old.filter((i) => i.id !== invite.id) : [],
  );
  queryClient.setQueriesData<SMPWInvite[]>({ queryKey: [QueryKeys.SMPWZoneInvites] }, (old) =>
    old ? old.filter((i) => i.id !== invite.id) : [],
  );
}

export const updateSMPWInviteCache = (queryClient: QueryClient, newInv: SMPWInvite) => {
  queryClient.setQueryData<SMPWInvite[]>([QueryKeys.SMPWZoneInvites], (old) => {
    if (!old) return [newInv];
    if (!old.some((inv) => inv.id === newInv.id)) return [...old, newInv];
    return old.map((inv) => (inv.id === newInv.id ? newInv : inv));
  });
};

export const updateSMPWLocationCache = (queryClient: QueryClient, newLoc: SMPWLocation) => {
  queryClient.setQueryData<SMPWLocation[]>([QueryKeys.SMPWLocations], (old) => {
    if (!old) return [newLoc];
    if (!old.some((l) => l.id === newLoc.id)) return [...old, newLoc];
    return old.map((l) => (l.id === newLoc.id ? newLoc : l));
  });
};

export function deleteFromSMPWLocationCache(queryClient: QueryClient, loc: SMPWLocation) {
  queryClient.setQueriesData<SMPWLocation[]>({ queryKey: [QueryKeys.SMPWLocations] }, (old) =>
    old ? old.filter((l) => l.id !== loc.id) : [],
  );
}

export const updateSMPWZoneDefaultCache = (queryClient: QueryClient, newZd: SMPWZoneDefault) => {
  queryClient.setQueryData<SMPWZoneDefault[]>([QueryKeys.SMPWZoneDefaults], (old) => {
    if (!old) return [newZd];
    if (!old.some((zd) => zd.id === newZd.id)) return [...old, newZd];
    return old.map((zd) => (zd.id === newZd.id ? newZd : zd));
  });
};

export function deleteFromSMPWZoneDefaultCache(queryClient: QueryClient, zd: SMPWZoneDefault) {
  queryClient.setQueriesData<SMPWZoneDefault[]>({ queryKey: [QueryKeys.SMPWZoneDefaults] }, (old) =>
    old ? old.filter((d) => d.id !== zd.id) : [],
  );
}

export const updateSMPWUserAvailabilities = (queryClient: QueryClient, newAvails: SMPWUserAvailability[]) => {
  if (!newAvails.length) return;
  const userId = newAvails[0]?.user_id;
  if (userId === undefined) return;

  queryClient.setQueryData<SMPWUserAvailability[]>([QueryKeys.SMPWUserAvailabilities], (old) => {
    if (!old) return newAvails;
    const filteredOld = old.filter((a) => a.user_id !== userId);
    return [...filteredOld, ...newAvails];
  });
};

export const updateSMPWLocSchedulesCache = (
  queryClient: QueryClient,
  zone_id: number,
  loc_id: number,
  incomingSchedules: SMPWSchedule[],
  from: string,
  to: string,
  crossPollinating = false,
): void => {
  if (!incomingSchedules.length) return;

  queryClient.setQueryData<SMPWSchedule[]>([QueryKeys.SMPWLocSchedules, loc_id, from, to], (existingSchedules) => {
    if (!existingSchedules) return incomingSchedules;
    const existingSchedulesMap = new Map(existingSchedules.map((schedule) => [schedule.id, schedule]));
    incomingSchedules.forEach((schedule) => {
      if (!crossPollinating) {
        existingSchedulesMap.set(schedule.id, schedule);
        return;
      }
      if (existingSchedulesMap.has(schedule.id)) {
        existingSchedulesMap.set(schedule.id, schedule);
      }
    });
    return Array.from(existingSchedulesMap.values());
  });

  if (!crossPollinating) {
    updateSMPWZoneSchedulesCache(queryClient, zone_id, incomingSchedules, from, to, true);
  }
};

export const updateSMPWZoneSchedulesCache = (
  queryClient: QueryClient,
  zone_id: number,
  incomingSchedules: SMPWSchedule[],
  from: string,
  to: string,
  crossPollinating = false,
): void => {
  if (!incomingSchedules.length) return;

  queryClient.setQueryData<SMPWSchedule[]>([QueryKeys.SMPWZoneSchedules, zone_id, from, to], (existingSchedules) => {
    if (!existingSchedules) return incomingSchedules;
    const existingSchedulesMap = new Map(existingSchedules.map((schedule) => [schedule.id, schedule]));
    incomingSchedules.forEach((schedule) => {
      if (!crossPollinating) {
        existingSchedulesMap.set(schedule.id, schedule);
        return;
      }
      if (existingSchedulesMap.has(schedule.id)) {
        existingSchedulesMap.set(schedule.id, schedule);
      }
    });
    return Array.from(existingSchedulesMap.values());
  });

  if (!crossPollinating) {
    updateSMPWLocSchedulesCache(
      queryClient,
      zone_id,
      incomingSchedules[0]!.location_id,
      incomingSchedules,
      from,
      to,
      true,
    );
  }
};

export const updateSMPWCongregations = (queryClient: QueryClient, newCong: Congregation) => {
  queryClient.setQueryData<Congregation[]>([QueryKeys.SMPWNearbyCongregations], (old) => {
    if (!old) return [newCong];
    if (!old.some((zd) => zd.id === newCong.id)) return [...old, newCong];
    return old.map((zd) => (zd.id === newCong.id ? newCong : zd));
  });
};
